import { Suspense, createElement, lazy, useRef } from 'react';
import type { ComponentType, ErrorInfo, LazyExoticComponent, ReactElement, ReactNode } from 'react';

import { ErrorBoundary, type ErrorBoundaryRenderProps } from '@/ui/components/ErrorBoundary';

import styles from './ViewHost.module.css';
import { panelDomId, tabDomId, type ShellView } from './types';

/**
 * Where a view panel is mounted — ADR-0020, docs/ARCHITECTURE.md §8, recon §4.2.
 *
 * Three guarantees, and they are the reason this is a component rather than three lines in
 * `App.tsx`:
 *
 * 1. **The panel is lazy.** `load` is called on first mount and never eagerly. Graph pulls
 *    D3, Chart pulls Chart.js, Map pulls Leaflet; an eager `Panel: ComponentType` in the
 *    descriptor would put all three into the main chunk of a user who wanted to format a
 *    config file. ADR-0020 calls this an architectural decision rather than an optimisation,
 *    and `ViewHost.test.tsx` asserts that the inactive view's loader is never called.
 *
 * 2. **Every panel has its OWN error boundary.** A crash in one visualisation must not take
 *    the toolbar down with it (SPEC §3). The boundary is keyed by view id, so switching tabs
 *    remounts it and a broken panel is escapable by leaving it — the legacy boundary had no
 *    `resetKeys` and no key, so its "Try Again" re-rendered the same broken subtree with the
 *    same props and threw again immediately.
 *
 * 3. **`unavailable` short-circuits before `load`.** That is `maxBytes` in ADR-0020 §4: a tab
 *    that cannot handle a 30 MB document says so INSTEAD of downloading its library and
 *    freezing the tab to discover it.
 *
 * ═══ WHY THE LAZY COMPONENTS ARE CACHED IN A REF ═══
 *
 * `lazy()` returns a NEW component type each call, and a new type means React unmounts the
 * old subtree and mounts a fresh one — the panel would lose all its state on every parent
 * render, and the module would be re-requested on every tab switch. Caching by view id makes
 * the component identity stable for the life of the host, which is what makes "switch away
 * and come back" cheap and what lets a panel keep its own `useState` (the Tree view's expanded
 * set, which ADR-0020 and SPEC §6.2 explicitly require to survive).
 *
 * ═══ WHY ViewHost TAKES ONE VIEW AND NOT THE REGISTRY ═══
 *
 * Its sibling `TabBar` takes the whole list. If this took the list too, the caller would have
 * to have run `select(doc)` for EVERY registered view in order to fill in `props` — including
 * the three heavy ones that are not on screen. Handing over only the active view makes
 * "inactive views cost nothing" a property of the signature.
 */

export interface ViewHostProps<P extends object> {
  /** The active view only. `props` is already `select(doc)`'s output. */
  readonly view: ShellView<P>;
  /**
   * The crash screen. Required and a function: `ui/**` does not translate, so there is no
   * built-in English fallback anywhere in this layer (ADR-0003, ADR-0018).
   */
  readonly fallback: (props: ErrorBoundaryRenderProps) => ReactNode;
  /** Shown while the module is in flight. Usually nothing: a local import resolves in a frame. */
  readonly pending?: ReactNode;
  /**
   * `true` when a tab strip is on screen, which is what makes this region a `tabpanel`. With
   * one registered view there is no `tablist`, and a `tabpanel` without one is a lie told to
   * a screen reader — so v1 renders a plain region.
   */
  readonly tabbed?: boolean;
  /** Any change clears a caught error. The app passes the document version. */
  readonly resetKeys?: readonly unknown[] | undefined;
  readonly onError?: ((error: Error, info: ErrorInfo) => void) | undefined;
  /** Localised `error.details` — the DEV-only stack disclosure summary. */
  readonly detailsLabel?: string | undefined;
  readonly className?: string | undefined;
}

export function ViewHost<P extends object>({
  view,
  fallback,
  pending,
  tabbed = false,
  resetKeys,
  onError,
  detailsLabel,
  className,
}: ViewHostProps<P>): ReactElement {
  const cache = useRef(new Map<string, LazyExoticComponent<ComponentType<P>>>());

  const region = className === undefined ? styles.host : `${styles.host} ${className}`;
  const shell = {
    id: panelDomId(view.id),
    className: region,
    ...(tabbed ? { role: 'tabpanel' as const, 'aria-labelledby': tabDomId(view.id) } : {}),
  };

  // Before the loader, not after: this is the whole point of the declarative size gate.
  if (view.unavailable !== undefined) {
    return <div {...shell}>{view.unavailable}</div>;
  }

  let Panel = cache.current.get(view.id);
  if (Panel === undefined) {
    Panel = lazy(view.load);
    cache.current.set(view.id, Panel);
  }

  const boundary = {
    ...(resetKeys === undefined ? {} : { resetKeys }),
    ...(onError === undefined ? {} : { onError }),
    ...(detailsLabel === undefined ? {} : { detailsLabel }),
  };

  return (
    <div {...shell}>
      {/*
        `key` is the view id, so a different view gets a different boundary INSTANCE: leaving
        a crashed panel and coming back is a clean mount rather than a boundary that is still
        holding the old error. `createElement` rather than `<Panel {...view.props} />` because
        spreading a generic `P` through JSX loses the relationship between the props object
        and the component that accepts it; this way `P` stays checked.
      */}
      <ErrorBoundary key={view.id} fallback={fallback} {...boundary}>
        <Suspense fallback={pending ?? null}>{createElement(Panel, view.props)}</Suspense>
      </ErrorBoundary>
    </div>
  );
}

export default ViewHost;
